/**
 * The 100×100 game board and occupancy index.
 *
 * The board doesn't store terrain — the original has none — but it tracks
 * which unit (if any) is at each cell so the rest of the engine can answer
 * "is this square free?" and "what's here?" in O(1).
 */

import { BOARD_SIZE, chebyshev, isOnBoard, type Position } from "./types.js";
import type { Unit } from "./units.js";

export class Board {
  /** Flat index → unitId, or -1 if empty. */
  private readonly cells: Int32Array;
  /** Per-owner base position. */
  readonly bases: [Position, Position];

  constructor(humanBase: Position, opponentBase: Position) {
    this.cells = new Int32Array(BOARD_SIZE * BOARD_SIZE).fill(-1);
    this.bases = [humanBase, opponentBase];
  }

  private idx(p: Position): number {
    return p.y * BOARD_SIZE + p.x;
  }

  occupantAt(p: Position): number {
    if (!isOnBoard(p)) return -1;
    const v = this.cells[this.idx(p)];
    return v === undefined ? -1 : v;
  }

  isEmpty(p: Position): boolean {
    return this.occupantAt(p) === -1;
  }

  /** Place a unit's id at a position. Returns false if the cell is taken or off-board. */
  place(unit: Unit): boolean {
    if (!isOnBoard(unit.position) || !this.isEmpty(unit.position)) return false;
    this.cells[this.idx(unit.position)] = unit.id;
    return true;
  }

  /** Clear the cell currently holding the given unit (if any). */
  remove(unit: Unit): void {
    if (!isOnBoard(unit.position)) return;
    const i = this.idx(unit.position);
    if (this.cells[i] === unit.id) this.cells[i] = -1;
  }

  /**
   * Move a unit from its current cell to a new cell. Returns false (and
   * leaves the board untouched) if the destination is off-board or occupied.
   */
  moveTo(unit: Unit, dest: Position): boolean {
    if (!isOnBoard(dest) || !this.isEmpty(dest)) return false;
    this.remove(unit);
    unit.position = dest;
    this.cells[this.idx(dest)] = unit.id;
    return true;
  }

  /** True iff `p` is one of the two base cells. */
  isBaseCell(p: Position): { isBase: true; owner: 0 | 1 } | { isBase: false } {
    if (p.x === this.bases[0].x && p.y === this.bases[0].y) return { isBase: true, owner: 0 };
    if (p.x === this.bases[1].x && p.y === this.bases[1].y) return { isBase: true, owner: 1 };
    return { isBase: false };
  }

  /**
   * Iterate every occupied cell within `radius` Chebyshev cells of `centre`
   * (exclusive of `centre` itself). Yields the cell position and the unit id.
   */
  *cellsWithin(centre: Position, radius: number): Generator<{ at: Position; unitId: number }> {
    const xMin = Math.max(0, centre.x - radius);
    const xMax = Math.min(BOARD_SIZE - 1, centre.x + radius);
    const yMin = Math.max(0, centre.y - radius);
    const yMax = Math.min(BOARD_SIZE - 1, centre.y + radius);
    for (let y = yMin; y <= yMax; y++) {
      for (let x = xMin; x <= xMax; x++) {
        if (x === centre.x && y === centre.y) continue;
        const id = this.cells[y * BOARD_SIZE + x];
        if (id !== undefined && id !== -1) yield { at: { x, y }, unitId: id };
      }
    }
  }
}

export { chebyshev, isOnBoard };
